import { Injectable, inject } from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

import {
  catchError,
  EMPTY,
  filter,
  finalize,
  Observable,
  Subject,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import { AuthToken } from '@core/models';
import { AuthService } from '@core/services/auth/auth.service';
import { environment } from '@env/environment';
import { Router } from '@angular/router';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private authService = inject(AuthService);

  private isRefreshing = false;
  private refreshTokenSubject: Subject<AuthToken | null> = new Subject();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.token;
    const isOldBaseUrl = req.url.includes(environment.baseUrl);
    const isNewBaseUrl = req.url.includes(environment.newBaseUrl);
    const isLoginEndpoint = req.url.includes('/auth/login');
    const isProfileEndpoint = req.url.includes('/auth/me');
    const isRefreshTokenRequest =
      req.url.includes('/auth/refresh') || req.url.includes('login/new_access_token');

    // Add token to all API requests except login and refresh endpoints
    if (token && (isOldBaseUrl || isNewBaseUrl) && !isLoginEndpoint && !isRefreshTokenRequest) {
      req = this.setToken(req, token.access_token);
    }

    return next.handle(req).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          // Handle 403 Forbidden - permission denied (user is authenticated but not authorized)
          if (error.status === 403) {
            // Don't logout or clear tokens for 403 errors
            // Just redirect to profile page and show the error message
            this.router.navigateByUrl('/profile/account');
            return throwError(() => error);
          }

          // Handle 401 Unauthorized - authentication problem
          if (error.status === 401) {
            // Don't logout on login endpoint - this is the initial auth
            if (isLoginEndpoint) {
              return throwError(() => error);
            }

            // For profile endpoint during login flow, don't logout immediately
            if (isProfileEndpoint) {
              // Allow the login flow to handle profile errors
              return throwError(() => error);
            }

            if (!isRefreshTokenRequest) {
              return this.handleUnauthorizedError(req, next);
            } else {
              this.authService.logout();
              this.router.navigateByUrl('/login');
            }
          }
        }
        return throwError(() => error);
      })
    );
  }

  handleUnauthorizedError(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((token: AuthToken) => {
          this.refreshTokenSubject.next(token);
          // Retry the original request with new token
          return next.handle(this.setToken(req, token.access_token));
        }),
        catchError((_error) => {
          console.error('Token refresh failed');
          // Logout here or clear cache
          this.authService.logout();
          this.router.navigateByUrl('/login');
          return EMPTY;
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token): token is AuthToken => token !== null),
        take(1),
        switchMap((token) => next.handle(this.setToken(req, token.access_token)))
      );
    }
  }

  private setToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}

export const tokenInterceptor = {
  provide: HTTP_INTERCEPTORS,
  useClass: TokenInterceptor,
  deps: [Router, AuthService],
  multi: true,
};
